import { getDomainFromHost, looksLikeLocalhost } from './url';

export function isLocalDev(): boolean {
  const domain = getDomainFromHost(window.location.host);
  return (
    looksLikeLocalhost(domain) ||
    domain === 'app.fullstory.test' ||
    domain === 'webber.internal.fullstory.test'
  );
}

export function isStaging(): boolean {
  const domain = getDomainFromHost(window.location.host);
  return (
    domain === 'app.staging.fullstory.com' || domain === 'webber.internal.staging.fullstory.com'
  );
}

/**
 * Determines an appropriate domain for playing back the page, different than
 * the current domain, in which playbacks will run. In the normal case, this
 * just changes subdomains from "app." to "play.".
 */
export function getPlaybackDomain(appDomain: string): string {
  // TODO(ianrose): Remember to update this once we also carve off a separate domain for playback.
  // TODO(jaime): Fairly certain we don't need to support 'www.' and 'fullstory.' anymore. 'app.' is ubiquitous. Consider ripping that out.
  if (appDomain.startsWith('app.')) {
    return `play.${appDomain.substr('app.'.length)}`;
  } else if (appDomain.startsWith('www.')) {
    return `play.${appDomain.substr('www.'.length)}`;
  } else if (appDomain.startsWith('webber.internal.')) {
    return `play.${appDomain.substr('webber.internal.'.length)}`;
  } else if (
    appDomain.startsWith('staging.') ||
    appDomain.startsWith('fullstory.') ||
    appDomain.startsWith('onfire.')
  ) {
    return `play.${appDomain}`;
  } else if (appDomain.startsWith('127.0.0.1') || appDomain.startsWith('localhost')) {
    // This is a special case where appDomain is a test environment or storybook. Manually
    // return the local dev playback domain.
    return 'play.fullstory.test:8043';
  } else if (appDomain.startsWith('dpistatstool.internal.')) {
    return `play.${appDomain.substring('dpistatstool.internal.'.length)}`;
  } else {
    throw new Error(
      `Unknown domain. Can't figure out how to run FsRenderer. Failing to be safe: ${appDomain}`,
    );
  }
}

/**
 * Determines an appropriate domain for the resource proxy endpoint. Different
 * than the current domain and the playback domain.
 */
export function getResourceProxyDomain(appDomain: string): string {
  if (isTestEnvironment(appDomain)) {
    if (appDomain.startsWith('app.')) {
      return `proxy.${appDomain.substr('app.'.length)}`;
    }
    if (appDomain.startsWith('webber.internal.')) {
      return `proxy.${appDomain.substr('webber.internal.'.length)}`;
    }
    return appDomain; // For the proxy endpoint, we just care that it is separate domain from the playback. Not a big deal though for local dev.
  }

  // Prod or staging.
  if (appDomain.startsWith('app.') || appDomain.startsWith('webber.internal.')) {
    if (appDomain.startsWith('app.staging') || appDomain.startsWith('webber.internal.staging')) {
      return 'proxy.staging-fsplayback.com';
    }
    if (appDomain.startsWith('app.onfire') || appDomain.startsWith('webber.internal.onfire')) {
      return 'proxy.playpen-fsplayback.com';
    }
    return 'proxy.fsplayback.com';
  }

  if (appDomain.startsWith('dpistatstool.internal.')) {
    return `proxy.${appDomain.substring('dpistatstool.internal.'.length)}`;
  }

  throw new Error(
    `Unknown domain. Can't figure out how to run FsRenderer. Failing to be safe: ${appDomain}`,
  );
}

export function isTestEnvironment(appDomain: string): boolean {
  return (
    appDomain.startsWith('app.fullstory.test') ||
    appDomain.startsWith('webber.internal.fullstory.test') ||
    appDomain.startsWith('127.0.0.1') ||
    appDomain.startsWith('localhost')
  );
}

export function isCypress(window: Window): boolean {
  return (window as any)['Cypress'] !== undefined;
}

// For elements inside an iframe ie. Playback renderer
export function isCypressInTop(window: Window): boolean {
  try {
    if (window.top && 'Cypress' in window.top) {
      return true;
    }
  } catch (e) {
    // Accessing 'window.parent' failed, almost certainly because it's
    // cross-origin. Swallow the exception and assume we're not running on
    // Cypress; this never happens there, because Cypress disables most security
    // features.
  }
  return false;
}
